// ============================================================================
// NPC System Types and Enums
// ============================================================================
// All values use enum constants - no magic strings
// ============================================================================
// ============================================================================
// NPC Category - High-level classification
// ============================================================================
export const NpcCategory = {
    PREY: 0, // Herbivores, small animals
    PREDATOR: 1, // Carnivores, hunters
    BIRD: 2, // Flying creatures
    AQUATIC: 3, // Water-dwelling
    UNDEAD: 4, // Skeletons, zombies, etc.
    HUMANOID: 5, // Bandits, goblins (future)
};
// ============================================================================
// NPC Type Registry
// ============================================================================
export const NpcType = {
    NONE: 0,
    // Small prey animals
    RABBIT: 1,
    SQUIRREL: 2,
    MOUSE: 3,
    RAT: 4,
    // Medium prey
    DEER: 10,
    BOAR: 11,
    GOAT: 12,
    SHEEP: 13,
    // Large herbivores
    HORSE: 20,
    COW: 21,
    MOOSE: 22,
    BISON: 23,
    // Small predators
    FOX: 30,
    WILDCAT: 31,
    BADGER: 32,
    WEASEL: 33,
    // Medium predators
    WOLF: 40,
    COYOTE: 41,
    LYNX: 42,
    HYENA: 43,
    // Large predators
    BEAR: 50,
    LION: 51,
    TIGER: 52,
    COUGAR: 53,
    // Birds - prey
    CHICKEN: 60,
    TURKEY: 61,
    PHEASANT: 62,
    DUCK: 63,
    GOOSE: 64,
    // Birds - predator
    HAWK: 70,
    EAGLE: 71,
    OWL: 72,
    RAVEN: 73,
    // Reptiles
    SNAKE: 80,
    LIZARD: 81,
    TURTLE: 82,
    CROCODILE: 83,
    // Aquatic
    FISH: 90,
    SALMON: 91,
    CRAB: 92,
    FROG: 93,
    // Insects (if ever needed)
    BEE: 100,
    SPIDER: 101,
    SCORPION: 102,
    // Undead (110+)
    SKELETON: 110,
    SKELETON_WARRIOR: 111, // Future: stronger variant
};
// ============================================================================
// Animal States
// ============================================================================
export const NpcState = {
    IDLE: 0,
    WANDERING: 1,
    GRAZING: 2, // For herbivores eating
    EATING: 2, // Alias for GRAZING
    HUNTING: 3, // For predators
    STALKING: 4, // Pre-hunt
    FLEEING: 5,
    FIGHTING: 6,
    SLEEPING: 7,
    RESTING: 8,
    DRINKING: 9,
    SOCIALIZING: 10,
    PATROLLING: 11, // Territory patrol
    DEFENDING: 12, // Defending territory/pack
    MATING: 13,
    CARING: 14, // Caring for young
    MIGRATING: 15,
    HIDING: 16,
    DEAD: 17,
    INJURED: 18,
    ALERT: 19, // Heard/smelled something
    INVESTIGATING: 20, // Checking something out
    FOLLOWING_PACK: 21, // Following pack leader
    RETURNING_TO_TERRITORY: 22, // Going back home
};
// Alias for backward compatibility with ECS components
export const NpcAIState = NpcState;
// ============================================================================
// Behavior Types
// ============================================================================
export const BehaviorType = {
    // Actions (0-49)
    NONE: 0,
    WANDER: 1, // Random movement
    FLEE: 2, // Running away from threat
    HUNT: 3, // Pursuing prey
    FOLLOW: 4, // Following pack/leader
    PATROL: 5, // Territory patrol
    GUARD: 6, // Guarding location
    SLEEP: 7, // Sleeping
    REST: 8, // Resting but aware
    GRAZE: 9, // Eating plants
    DRINK: 10, // Drinking water
    STALK: 11, // Sneaking toward prey
    ATTACK: 12, // Active combat
    DEFEND: 13, // Defensive combat
    HIDE: 14, // Finding cover
    SEEK_SHELTER: 15, // Looking for shelter
    RETURN_HOME: 16, // Going back to den/nest
    INVESTIGATE: 17, // Checking out something
    SOCIALIZE: 18, // Interacting with same species
    MATE: 19, // Mating behavior
    NURTURE: 20, // Caring for young
    SCAVENGE: 21, // Looking for carrion
    PLAY: 22, // Playing (young animals)
    TERRITORIAL_DISPLAY: 23, // Warning other animals
    SUNBATHE: 24, // Cold-blooded animals
    BURROW: 25, // Digging behavior
    // Personality traits (50-99)
    COWARDLY: 50, // Flees easily
    AGGRESSIVE: 51, // Quick to attack
    TERRITORIAL: 52, // Defends territory
    PROTECTIVE: 53, // Defends pack/young
    CUNNING: 54, // Uses clever tactics
    STEALTHY: 55, // Sneaky movement
    INTELLIGENT: 56, // Learns and adapts
    FAST_RUNNER: 57, // High sprint speed
    AGILE: 58, // Good at dodging
    ALERT: 59, // High awareness
    ADAPTABLE: 60, // Survives in many conditions
    POWERFUL: 61, // High damage output
    PACK_HUNTER: 62, // Hunts in groups
    AMBUSH_PREDATOR: 63, // Waits and strikes
    VENOMOUS: 64, // Uses poison attacks
    ARMORED: 65, // High natural defense
    FLOCKING: 66, // Stays with group
    AQUATIC: 67, // Water-adapted
    AERIAL: 68, // Flight-capable
    NOCTURNAL: 69, // Active at night
    KEEN_VISION: 70, // Excellent sight range
};
// ============================================================================
// Diet Types
// ============================================================================
export const DietType = {
    HERBIVORE: 0,
    CARNIVORE: 1,
    OMNIVORE: 2,
    INSECTIVORE: 3,
    PISCIVORE: 4, // Fish eater
    SCAVENGER: 5,
    FILTER_FEEDER: 6,
    NONE: 7, // Undead/constructs don't eat
};
// ============================================================================
// Habitat Types
// ============================================================================
export const HabitatType = {
    LAND_ONLY: 0,
    WATER_ONLY: 1,
    AMPHIBIOUS: 2,
    ARBOREAL: 3, // Tree-dwelling
    UNDERGROUND: 4,
    AERIAL: 5, // Flying
    COASTAL: 6, // Beach/shore
};
// ============================================================================
// Social Types
// ============================================================================
export const SocialType = {
    SOLITARY: 0,
    PACK: 1,
    HERD: 2,
    FLOCK: 3,
    COLONY: 4,
    PAIR: 5, // Mated pairs
    FAMILY: 6, // Family groups
    TERRITORIAL: 7, // Defends territory alone
};
// ============================================================================
// Activity Period
// ============================================================================
export const ActivityPeriod = {
    DIURNAL: 0, // Active during day
    NOCTURNAL: 1, // Active at night
    CREPUSCULAR: 2, // Active at dawn/dusk
    CATHEMERAL: 3, // Active any time
};
// ============================================================================
// Aggression Level
// ============================================================================
export const AggressionLevel = {
    PASSIVE: 0, // Never attacks
    DEFENSIVE: 1, // Only attacks if attacked
    TERRITORIAL: 2, // Attacks if territory invaded
    PROTECTIVE: 3, // Attacks if young/pack threatened
    OPPORTUNISTIC: 4, // Attacks if prey is weak/alone
    AGGRESSIVE: 5, // Attacks on sight
    BERSERKER: 6, // Attacks everything (rare/special)
};
// ============================================================================
// Pack Role
// ============================================================================
export const PackRole = {
    NONE: 0,
    ALPHA: 1,
    BETA: 2,
    MEMBER: 3,
    OMEGA: 4, // Lowest rank
    SCOUT: 5,
    HUNTER: 6,
    GUARD: 7,
    ELDER: 8,
    YOUNG: 9,
};
// ============================================================================
// Animal Size Category
// ============================================================================
export const AnimalSize = {
    TINY: 0, // Insects, mice
    SMALL: 1, // Rabbits, squirrels
    MEDIUM: 2, // Dogs, foxes, deer
    LARGE: 3, // Wolves, big cats
    HUGE: 4, // Bears, moose
    MASSIVE: 5, // Elephants (if ever added)
};
// ============================================================================
// Attack Type
// ============================================================================
export const AnimalAttackType = {
    BITE: 0,
    CLAW: 1,
    GORE: 2, // Horns/tusks
    KICK: 3,
    TRAMPLE: 4,
    STING: 5,
    VENOM: 6,
    CONSTRICT: 7,
    PECK: 8,
    CHARGE: 9,
    TAIL_WHIP: 10,
};
// ============================================================================
// Threat Response
// ============================================================================
export const ThreatResponse = {
    FLEE: 0, // Always run
    FIGHT: 1, // Always fight
    FIGHT_OR_FLIGHT: 2, // Depends on health/numbers
    FREEZE: 3, // Play dead or freeze
    BLUFF: 4, // Try to scare off
    CALL_PACK: 5, // Alert others first
    HIDE: 6, // Find cover
    SUBMIT: 7, // Show submission
};
// ============================================================================
// Sense Priorities
// ============================================================================
export const SensePriority = {
    SIGHT: 0,
    HEARING: 1,
    SMELL: 2,
    VIBRATION: 3, // Ground vibrations
    HEAT: 4, // Heat sensing (snakes)
    ECHOLOCATION: 5,
};
// ============================================================================
// Pack State
// ============================================================================
export const PackState = {
    IDLE: 0,
    HUNTING: 1,
    MIGRATING: 2,
    DEFENDING: 3,
    RESTING: 4,
    GATHERING: 5, // Regrouping
};
// Note: Network serialization uses NpcSnapshot from protocol/messages.ts
//# sourceMappingURL=types.js.map